import * as React from "react"
import { createRoot } from "react-dom/client"
import { ROUTES, FALLBACK_ORIGIN, routeForPath, clean } from "./routes"
import WorkPage from "./work/WorkPage"
import { WORK_PAGES, pageTheme } from "./work/pages"
import Home from "./home/Home"
import About from "./about/About"
import AboutVideo, { preloadAbout } from "./about/AboutVideo"
import FeedPage from "./feed/FeedPage"
import { HOME } from "./home/content"
import { Shell, type ShellApi } from "./shell/Shell"
import { ShellContext, useMobile, type IntroNodes, type ShellState, type Tone } from "./shell/context"
import { navigate as runTransition, arrived, changing, type PageInfo, type TransitionHost } from "./app/transition"
import { Sections } from "./work/sections"
import { setBars, fadeBars } from "./app/bars"
import "./base.css"

const HOME_STOPS = [
    [233, 233, 233, 0],
    [233, 233, 233, 1],
]
const ABOUT_STOPS = [
    [0, 0, 0, 0],
    [0, 0, 0, 1],
]
function pageInfo(routeId: string): PageInfo {
    const work = WORK_PAGES[ROUTES[routeId]?.page]
    if (work) {
        const t = pageTheme(work)
        return { kind: "work", tone: t.tone, stops: t.stops }
    }
    if (["about", "aboutOld", "about2"].includes(ROUTES[routeId]?.page)) return { kind: "about", tone: "dark", stops: ABOUT_STOPS }
    // (the feed: no home intro nor work chrome, so as the about; light like the home)
    if (ROUTES[routeId]?.page === "feed") return { kind: "about", tone: "home", stops: HOME_STOPS }
    return { kind: "home", tone: "home", stops: HOME_STOPS }
}

const initialRoute = routeForPath(location.pathname) || "notFound"
// an old address (e.g. /27-home): show the page under its own address
if (ROUTES[initialRoute] && clean(location.pathname) !== ROUTES[initialRoute].path)
    history.replaceState(null, "", ROUTES[initialRoute].path + location.search + location.hash)

function App() {
    const [routeId, setRouteId] = React.useState(initialRoute)
    // a work page is mounted fresh on a page change (transition, back/forward),
    // not when it carries on into the next category by scrolling
    const [feed, setFeed] = React.useState(0)
    const current = React.useRef(routeId)
    const carried = React.useRef(false)
    current.current = routeId
    const mobile = useMobile()
    const [menuOpen, setMenuOpen] = React.useState(false)
    const intro = React.useRef<IntroNodes>({})
    const shellApi = React.useRef<ShellApi>(null)
    const first = React.useMemo(() => {
        const i = pageInfo(initialRoute)
        return { kind: i.kind, tone: i.tone as Tone }
    }, [])

    // what the page transition needs from the app (src/app/transition.ts)
    const host = React.useMemo<TransitionHost>(
        () => ({
            current: () => current.current,
            info: pageInfo,
            swap(id, hash) {
                history.pushState({ id }, "", ROUTES[id].path + (hash ? "#" + hash : ""))
                setMenuOpen(false)
                setFeed((f) => f + 1)
                setRouteId(id)
            },
            shell: {
                headerMix: (to, d) => shellApi.current?.headerMix(to, d),
                headerTone: (t, fade) => shellApi.current?.headerTone(t, fade),
                chromeTone: (t) => shellApi.current?.chromeTone(t),
                leaveForHome: () => shellApi.current?.leaveForHome(),
                leaveWork: () => shellApi.current?.leaveWork(),
                chromeMix: (t, d, l) => shellApi.current?.chromeMix(t, d, l),
            },
        }),
        []
    )

    /** Go to a path: our pages with the transition, others on the Framer site. */
    const navigate = React.useCallback((href: string) => {
        const url = new URL(href, location.href)
        if (url.origin !== location.origin) return false
        const id = routeForPath(url.pathname)
        if (!id) {
            location.assign(FALLBACK_ORIGIN + url.pathname + url.search + url.hash)
            return true
        }
        if (id === current.current || ROUTES[id].page === ROUTES[current.current]?.page) return true
        runTransition(host, id, url.hash.slice(1) || undefined)
        return true
    }, [])

    const shell = React.useMemo<ShellState & { prepareHomeIntro: () => void }>(
        () => ({
            menuOpen,
            setMenuOpen,
            intro,
            navigate,
            mobile,
            prepareHomeIntro: () => shellApi.current?.prepareHomeIntro(),
            continueTo: (href: string, back?: boolean) => {
                const id = routeForPath(new URL(href, location.href).pathname)
                if (!id || id === current.current || changing() || Sections.leaving) return
                // the work page carries on in place (same feed, no remount):
                // on into a new category is a new entry, back up into one the same
                if (back) history.replaceState({ id }, "", ROUTES[id].path)
                else history.pushState({ id }, "", ROUTES[id].path)
                setMenuOpen(false)
                // same page on screen: its colours are already fading over (caseColors)
                carried.current = true
                setRouteId(id)
            },
            caseColors: (tone: Tone, stops: number[][], progress?: () => number | null, instant?: boolean) => {
                shellApi.current?.headerTone(tone, !instant)
                shellApi.current?.chromeTone(tone, instant)
                // the bars follow the background fading in, frame by frame
                fadeBars(stops, instant ? 1 : 600, instant ? undefined : progress)
            },
        }),
        [menuOpen, mobile]
    )

    React.useEffect(() => {
        history.scrollRestoration = "manual"
        // back/forward: straight to the page
        const onPop = () => {
            setFeed((f) => f + 1)
            setRouteId(routeForPath(location.pathname) || "notFound")
        }
        addEventListener("popstate", onPop)
        // plain links in page content go through the same navigation
        const onClick = (e: MouseEvent) => {
            if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
            const a = (e.target as Element)?.closest?.("a[href]") as HTMLAnchorElement | null
            if (!a || a.target === "_blank") return
            if (navigate(a.href)) e.preventDefault()
        }
        addEventListener("click", onClick)
        // the about loading ahead: a pointer on (or focus on) a link to it
        const toAbout = (e: Event) => {
            const a = (e.target as Element)?.closest?.("a[href]") as HTMLAnchorElement | null
            if (a && routeForPath(new URL(a.href, location.href).pathname) === "about") preloadAbout()
        }
        addEventListener("pointerover", toAbout, { passive: true })
        addEventListener("focusin", toAbout)
        return () => {
            removeEventListener("pointerover", toAbout)
            removeEventListener("focusin", toAbout)
            removeEventListener("popstate", onPop)
            removeEventListener("click", onClick)
        }
    }, [])

    // (and the menu opening: About is one of its links)
    React.useEffect(() => {
        if (menuOpen && ROUTES[routeId]?.page !== "about") preloadAbout()
    }, [menuOpen])

    const route = ROUTES[routeId]
    const info = pageInfo(routeId)
    // a new page is on screen: its colour behind everything (and behind
    // Safari's bars), then hand over the covering layer
    React.useLayoutEffect(() => {
        if (!route) return
        if (carried.current) return void (carried.current = false)
        const st = document.getElementById("nto-page-style")
        if (st && info.kind === "home") st.textContent = `html body { background: ${HOME.background}; }`
        if (st && info.kind === "about") st.textContent = `html body { background: ${route.page === "feed" ? HOME.background : "#000"}; }`
        setBars(info.stops)
        arrived(info.kind)
    }, [routeId])

    if (!route) {
        // not rebuilt yet: hand over to the Framer site
        if (new URL(FALLBACK_ORIGIN).host === location.host) return null
        location.replace(FALLBACK_ORIGIN + location.pathname + location.search + location.hash)
        return null
    }
    const work = WORK_PAGES[route.page]
    return (
        <ShellContext.Provider value={shell}>
            {work ? (
                <WorkPage key={"feed" + feed} page={work} />
            ) : route.page === "about" ? (
                <AboutVideo key={route.page + feed} />
            ) : route.page === "feed" ? (
                <FeedPage key={route.page + feed} />
            ) : route.page === "aboutOld" || route.page === "about2" ? (
                <About key={route.page + feed} variant={route.page === "about2" ? "fly" : "deck"} />
            ) : (
                <Home key={routeId} {...HOME} />
            )}
            <Shell ref={shellApi} kind={info.kind} page={route.page} category={work ? work.label : "Sports"} tone={info.tone} first={first} />
        </ShellContext.Provider>
    )
}

createRoot(document.getElementById("main")!).render(<App />)
